import React, { useEffect } from 'react'

import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
	useAlegraColIdentificationTypes,
	useKindOfPersonValues,
	useRegimeValues,
} from '@/core/hooks/misc/useAlegraConstants'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'

import { BillingInformationFormData } from '../BillingInformation.types'

interface AlegraColFieldsProps {
	setKindOfPerson: (value: string | undefined) => void
	kindOfPerson: string | undefined
	isEditMode?: boolean
}

export const AlegraColFields: React.FC<AlegraColFieldsProps> = ({
	setKindOfPerson,
	kindOfPerson,
	isEditMode = false,
}) => {
	const { t } = useTranslation('common')
	const {
		register,
		control,
		formState: { errors },
		watch,
	} = useFormContext<BillingInformationFormData>()

	const alegraColIdentificationTypes = useAlegraColIdentificationTypes()
	const kindOfPersonValues = useKindOfPersonValues()
	const regimeValues = useRegimeValues()

	// Watch kind of person changes to update regime values
	useEffect(() => {
		const subscription = watch((value, { name }) => {
			if (name === 'alegra_kind_of_person') {
				setKindOfPerson(value.alegra_kind_of_person)
			}
		})
		return () => subscription.unsubscribe()
	}, [watch, setKindOfPerson])

	return (
		<>
			<div className="flex flex-col gap-2">
				<Label htmlFor="alegra_identification_type_col">
					{t('account.billing_information.alegra_identification_type')} *
				</Label>
				<Controller
					name="alegra_identification_type_col"
					control={control}
					render={({ field }) => (
						<Select
							value={field.value || ''}
							onValueChange={field.onChange}
							disabled={isEditMode}
						>
							<SelectTrigger className="bg-input w-full">
								<SelectValue
									placeholder={t(
										'account.billing_information.select_identification_type'
									)}
								/>
							</SelectTrigger>
							<SelectContent>
								{alegraColIdentificationTypes.map((type) => (
									<SelectItem key={type.value} value={type.value}>
										{type.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				/>
				{errors.alegra_identification_type_col && (
					<p className="text-sm text-destructive">
						{errors.alegra_identification_type_col.message}
					</p>
				)}
			</div>
			<div className="flex flex-col gap-2">
				<Label htmlFor="alegra_identification_verification_digit">
					{t(
						'account.billing_information.alegra_identification_verification_digit'
					)}{' '}
					*
				</Label>
				<Input
					id="alegra_identification_verification_digit"
					{...register('alegra_identification_verification_digit')}
					className="bg-input"
					maxLength={1}
					placeholder="0-9"
					disabled={isEditMode}
					aria-invalid={!!errors.alegra_identification_verification_digit}
				/>
				{errors.alegra_identification_verification_digit && (
					<p className="text-sm text-destructive">
						{errors.alegra_identification_verification_digit.message}
					</p>
				)}
			</div>
			<div className="flex flex-col gap-2 md:col-span-2">
				<Label htmlFor="alegra_kind_of_person">
					{t('account.billing_information.alegra_kind_of_person')} *
				</Label>
				<Controller
					name="alegra_kind_of_person"
					control={control}
					render={({ field }) => (
						<Select
							value={field.value || ''}
							onValueChange={(value) => {
								field.onChange(value)
								setKindOfPerson(value)
							}}
							disabled={isEditMode}
						>
							<SelectTrigger className="bg-input w-full">
								<SelectValue
									placeholder={t(
										'account.billing_information.select_kind_of_person'
									)}
								/>
							</SelectTrigger>
							<SelectContent>
								{kindOfPersonValues.map((kind) => (
									<SelectItem key={kind.value} value={kind.value}>
										{kind.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				/>
				{errors.alegra_kind_of_person && (
					<p className="text-sm text-destructive">
						{errors.alegra_kind_of_person.message}
					</p>
				)}
			</div>
			<div className="flex flex-col gap-2 md:col-span-2">
				<Label htmlFor="alegra_regime">
					{t('account.billing_information.alegra_regime')} *
				</Label>
				<Controller
					name="alegra_regime"
					control={control}
					render={({ field }) => (
						<Select
							value={field.value || ''}
							onValueChange={field.onChange}
							disabled={!kindOfPerson}
						>
							<SelectTrigger className="bg-input w-full">
								<SelectValue
									placeholder={t('account.billing_information.select_regime')}
								/>
							</SelectTrigger>
							<SelectContent>
								{regimeValues.map((regime) => (
									<SelectItem key={regime.value} value={regime.value}>
										{regime.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				/>
				{errors.alegra_regime && (
					<p className="text-sm text-destructive">
						{errors.alegra_regime.message}
					</p>
				)}
			</div>
			<div className="flex flex-col gap-2">
				<Label htmlFor="alegra_firstname">
					{t('account.billing_information.alegra_firstname')} *
				</Label>
				<Input
					id="alegra_firstname"
					{...register('alegra_firstname')}
					className="bg-input"
					maxLength={40}
				/>
				{errors.alegra_firstname && (
					<p className="text-sm text-destructive">
						{errors.alegra_firstname.message}
					</p>
				)}
			</div>
			<div className="flex flex-col gap-2">
				<Label htmlFor="alegra_lastname">
					{t('account.billing_information.alegra_lastname')} *
				</Label>
				<Input
					id="alegra_lastname"
					{...register('alegra_lastname')}
					className="bg-input"
					maxLength={40}
				/>
				{errors.alegra_lastname && (
					<p className="text-sm text-destructive">
						{errors.alegra_lastname.message}
					</p>
				)}
			</div>
		</>
	)
}
